import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Section from '../components/Section';
import {
  CheckCircle,
  TrendingUp,
  Scale,
  Handshake,
  Building2,
  Send,
  ArrowRight,
} from 'lucide-react';
import { submitToSanity } from '../services/api';

interface FormData {
  fullName: string;
  businessName: string;
  countryDistrict: string;
  commodities: string[];
  annualCapacity: string;
  certifications: string;
  phone: string;
  email: string;
  message: string;
}

interface FormErrors {
  fullName?: string;
  businessName?: string;
  countryDistrict?: string;
  commodities?: string;
  annualCapacity?: string;
  phone?: string;
  email?: string;
  message?: string;
}

const COMMODITY_OPTIONS = [
  'Rice',
  'Beans & Peas',
  'Maize',
  'Groundnuts',
  'Sugar',
  'Other',
];

const valuePropositions = [
  {
    icon: CheckCircle,
    title: 'Reliable Payback on Delivery',
    description:
      'Payments processed promptly on confirmed delivery. No delayed settlements, no ambiguity.',
  },
  {
    icon: TrendingUp,
    title: 'Consistent Volume Demand',
    description:
      'We operate on structured procurement cycles, giving you predictable offtake and reduced market risk.',
  },
  {
    icon: Scale,
    title: 'Fair, Market Based Pricing',
    description:
      'Prices tied to real market benchmarks. We negotiate transparently and share the reasoning behind every rate.',
  },
  {
    icon: Handshake,
    title: 'Long Term Partnership Structure',
    description:
      'We build lasting supplier relationships, not one off transactions. Consistent performance leads to framework agreements.',
  },
  {
    icon: Building2,
    title: 'Access to Institutional Buyers',
    description:
      'Through Revian, your grain reaches NGOs, government programmes, food processors, and regional distributors at scale.',
  },
];

const onboardingSteps = [
  { label: 'Apply', number: '01' },
  { label: 'Quality Assessment', number: '02' },
  { label: 'Trial Shipment', number: '03' },
  { label: 'Partnership Agreement', number: '04' },
];

const EMPTY_FORM: FormData = {
  fullName: '',
  businessName: '',
  countryDistrict: '',
  commodities: [],
  annualCapacity: '',
  certifications: '',
  phone: '',
  email: '',
  message: '',
};

const SuppliersCircle: React.FC = () => {
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleCheckbox = (commodity: string) => {
    setFormData((prev) => {
      const updated = prev.commodities.includes(commodity)
        ? prev.commodities.filter((c) => c !== commodity)
        : [...prev.commodities, commodity];
      return { ...prev, commodities: updated };
    });
    if (errors.commodities) {
      setErrors((prev) => ({ ...prev, commodities: undefined }));
    }
  };

  const validate = (): FormErrors => {
    const newErrors: FormErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required.';
    if (!formData.businessName.trim())
      newErrors.businessName = 'Business or farm name is required.';
    if (!formData.countryDistrict.trim())
      newErrors.countryDistrict = 'Country or district is required.';
    if (formData.commodities.length === 0)
      newErrors.commodities = 'Select at least one commodity.';
    if (!formData.annualCapacity || Number(formData.annualCapacity) <= 0)
      newErrors.annualCapacity = 'Provide a valid annual supply capacity.';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required.';
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email address.';
    }
    if (!formData.message.trim())
      newErrors.message = 'Please add a brief message or notes.';
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await submitToSanity('supplierSubmission', {
        fullName: formData.fullName,
        businessName: formData.businessName,
        countryDistrict: formData.countryDistrict,
        commodities: formData.commodities,
        annualCapacity: formData.annualCapacity,
        certifications: formData.certifications,
        phone: formData.phone,
        email: formData.email,
        message: formData.message,
      });

      setSubmitStatus('success');
      setFormData(EMPTY_FORM);
      setErrors({});
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (field: keyof FormErrors) =>
    `w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-red focus:border-transparent transition-all ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-gray-300'
    }`;

  return (
    <main className="pt-20">
      <Section padding="default">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-playfair font-bold text-primary-black mb-6">
            Become a{' '}
            <span className="text-primary-red">Supplier</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-8">
            Join Revian's network of verified grain producers and aggregators
            across East Africa.
          </p>
          <button
            onClick={() => document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary-red hover:bg-red-700 text-white font-inter font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
          >
            Apply Now
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </motion.div>
        {/* Onboarding Process */}
        <div className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-10"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-primary-black mb-4">
              The Onboarding Process
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl">
              From first contact to active Partnership. A structured four‑step journey
              designed to be clear, fair, and efficient.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {onboardingSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="relative group"
              >
                <div className="bg-cream-50 group-hover:bg-white border border-transparent group-hover:border-gray-200 group-hover:shadow-md rounded-xl p-6 h-full text-center transition-all duration-250">
                  <div className="text-5xl font-playfair font-bold text-primary-red/20 group-hover:text-primary-red/40 mb-3 transition-colors duration-250">
                    {step.number}
                  </div>
                  <h3 className="text-lg font-playfair font-bold text-primary-black group-hover:text-primary-red transition-colors duration-250">
                    {step.label}
                  </h3>
                </div>
                {index < onboardingSteps.length - 1 && (
                  <div className="hidden md:flex absolute top-1/2 -right-3 -translate-y-1/2 items-center justify-center z-10">
                    <ArrowRight className="w-5 h-5 text-secondary-terracotta" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Two-column layout: value proposition + form */}
        <div id="apply" className="grid lg:grid-cols-2 gap-12 mb-20 items-start">
          {/* Left: Value Proposition */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-playfair font-bold text-primary-black mb-3">
              Why Supply Through Revian?
            </h2>
            <p className="text-gray-700 leading-relaxed mb-8">
              We work with grain producers and aggregators who value reliability
              as much as we do. Here is what being a Revian supplier means in
              practice.
            </p>
            <div className="space-y-4">
              {valuePropositions.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                    className="flex items-start gap-4 bg-white rounded-xl p-4 shadow-sm border border-gray-100"
                  >
                    <div className="w-10 h-10 rounded-full bg-primary-red/10 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-primary-red" />
                    </div>
                    <div>
                      <h4 className="font-playfair font-bold text-primary-black mb-1">
                        {item.title}
                      </h4>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Right: Supplier Interest Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-8">
              <h2 className="text-2xl font-playfair font-bold text-primary-black mb-6">
                Supplier Interest Form
              </h2>

              {submitStatus === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="text-center py-12"
                >
                  <div className="w-16 h-16 rounded-full bg-secondary-sage/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-secondary-sage" />
                  </div>
                  <h3 className="text-2xl font-playfair font-bold text-primary-black mb-3">
                    Interest Submitted
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Thank you for your interest in supplying through Revian. Our
                    procurement team will review your details and contact you
                    within 3 business days.
                  </p>
                  <button
                    onClick={() => setSubmitStatus('idle')}
                    className="text-primary-red font-inter font-medium hover:underline"
                  >
                    Submit another response
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} noValidate className="space-y-5">
                  {/* Full Name */}
                  <div>
                    <label
                      htmlFor="fullName"
                      className="block text-sm font-inter font-medium text-gray-700 mb-1.5"
                    >
                      Full Name <span className="text-primary-red">*</span>
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="John Mukasa"
                      className={inputClass('fullName')}
                    />
                    {errors.fullName && (
                      <p className="mt-1 text-xs text-red-600">{errors.fullName}</p>
                    )}
                  </div>

                  {/* Business / Farm Name */}
                  <div>
                    <label
                      htmlFor="businessName"
                      className="block text-sm font-inter font-medium text-gray-700 mb-1.5"
                    >
                      Business / Farm Name <span className="text-primary-red">*</span>
                    </label>
                    <input
                      type="text"
                      id="businessName"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleChange}
                      placeholder="Mukasa Grains Ltd"
                      className={inputClass('businessName')}
                    />
                    {errors.businessName && (
                      <p className="mt-1 text-xs text-red-600">{errors.businessName}</p>
                    )}
                  </div>

                  {/* Country / District */}
                  <div>
                    <label
                      htmlFor="countryDistrict"
                      className="block text-sm font-inter font-medium text-gray-700 mb-1.5"
                    >
                      Country / District <span className="text-primary-red">*</span>
                    </label>
                    <input
                      type="text"
                      id="countryDistrict"
                      name="countryDistrict"
                      value={formData.countryDistrict}
                      onChange={handleChange}
                      placeholder="Uganda, Mbarara District"
                      className={inputClass('countryDistrict')}
                    />
                    {errors.countryDistrict && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.countryDistrict}
                      </p>
                    )}
                  </div>

                  {/* Commodities Offered */}
                  <div>
                    <span className="block text-sm font-inter font-medium text-gray-700 mb-2">
                      Commodities Offered <span className="text-primary-red">*</span>
                    </span>
                    <div className="grid grid-cols-2 gap-2">
                      {COMMODITY_OPTIONS.map((commodity) => (
                        <label
                          key={commodity}
                          className="flex items-center gap-2.5 cursor-pointer group"
                        >
                          <input
                            type="checkbox"
                            checked={formData.commodities.includes(commodity)}
                            onChange={() => handleCheckbox(commodity)}
                            className="w-4 h-4 accent-primary-red rounded"
                          />
                          <span className="text-sm text-gray-700 group-hover:text-primary-black transition-colors">
                            {commodity}
                          </span>
                        </label>
                      ))}
                    </div>
                    {errors.commodities && (
                      <p className="mt-1 text-xs text-red-600">{errors.commodities}</p>
                    )}
                  </div>

                  {/* Estimated Annual Supply Capacity */}
                  <div>
                    <label
                      htmlFor="annualCapacity"
                      className="block text-sm font-inter font-medium text-gray-700 mb-1.5"
                    >
                      Estimated Annual Supply Capacity (tons){' '}
                      <span className="text-primary-red">*</span>
                    </label>
                    <input
                      type="number"
                      id="annualCapacity"
                      name="annualCapacity"
                      value={formData.annualCapacity}
                      onChange={handleChange}
                      min="1"
                      placeholder="e.g. 500"
                      className={inputClass('annualCapacity')}
                    />
                    {errors.annualCapacity && (
                      <p className="mt-1 text-xs text-red-600">{errors.annualCapacity}</p>
                    )}
                  </div>

                  {/* Certifications */}
                  <div>
                    <label
                      htmlFor="certifications"
                      className="block text-sm font-inter font-medium text-gray-700 mb-1.5"
                    >
                      Current Certifications Held{' '}
                      <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      id="certifications"
                      name="certifications"
                      value={formData.certifications}
                      onChange={handleChange}
                      placeholder="e.g. UNBS, Organic, Phytosanitary"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-red focus:border-transparent transition-all"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-inter font-medium text-gray-700 mb-1.5"
                    >
                      Phone Number <span className="text-primary-red">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+256 700 000 000"
                      className={inputClass('phone')}
                    />
                    {errors.phone && (
                      <p className="mt-1 text-xs text-red-600">{errors.phone}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-inter font-medium text-gray-700 mb-1.5"
                    >
                      Email Address <span className="text-primary-red">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@mukasagrains.com"
                      className={inputClass('email')}
                    />
                    {errors.email && (
                      <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-inter font-medium text-gray-700 mb-1.5"
                    >
                      Message / Additional Notes{' '}
                      <span className="text-primary-red">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Tell us about your operation, typical harvest seasons, storage capacity, or any other relevant information..."
                      className={`${inputClass('message')} resize-none`}
                    />
                    {errors.message && (
                      <p className="mt-1 text-xs text-red-600">{errors.message}</p>
                    )}
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-primary-red hover:bg-red-700 text-white font-inter font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      'Submitting...'
                    ) : (
                      <>
                        Submit Interest
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </button>

                  {/* Error Message */}
                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-red-50 border border-red-300 rounded-lg text-center"
                    >
                      <p className="text-red-700 font-medium">
                        Failed to submit. Please try again or contact us directly.
                      </p>
                    </motion.div>
                  )}
                </form>
              )}
            </div>
          </motion.div>
        </div>

        
      </Section>
    </main>
  );
};

export default SuppliersCircle;
